import { SignOut, House, ListChecks, VideoCamera, List, X, Path, Gift } from "@phosphor-icons/react";
import { useAuth } from "../../hooks/useAuth";
import { Link, useLocation } from "react-router";
import clsx from "clsx";

const NAV_ITEMS = [
  { path: "/", icon: House, label: "Anasayfa" },
  { path: "/mufredat", icon: Path, label: "Yol Haritam" },
  { path: "/hepsi", icon: ListChecks, label: "Tüm Dersler" },
  { path: "/canli-dersler", icon: VideoCamera, label: "Canlı Dersler" },
];

export default function DashboardSidebar({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const { user, logout } = useAuth();
  const location = useLocation();

  const initials = user?.shortName
    ?.split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) || "?";

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-20 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={clsx(
          "w-56 h-screen flex flex-col max-lg:bg-surface-1",
          "fixed z-30 transition-transform duration-300",
          isOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        )}
      >
        {/* Logo + Close */}
        <div className="flex items-center px-5 h-16 shrink-0 relative">
          <img
            src="/c2w_colorful_2.svg"
            alt="Code2Work"
            className="h-7 w-auto"
          />
          <button
            onClick={onClose}
            className="lg:hidden absolute right-4 p-1.5 text-white/30 hover:text-white/60 transition-colors cursor-pointer"
          >
            <X size={18} />
          </button>
        </div>

        {/* Nav items */}
        <nav className="flex-1 flex flex-col gap-0.5 px-3 pt-2">
          {NAV_ITEMS.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={onClose}
                className={clsx(
                  "flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors cursor-pointer text-sm",
                  isActive
                    ? "bg-white/[0.08] text-white font-semibold"
                    : "text-white/40 hover:text-white/70 hover:bg-white/5"
                )}
              >
                <item.icon size={20} weight={isActive ? "fill" : "regular"} />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>

        {/* User + Logout (bottom) */}
        <div className="shrink-0 px-4 pb-4 space-y-3">
          <Link
            to="/arkadasini-oner"
            onClick={onClose}
            className={clsx(
              "flex items-center gap-3 px-3 py-2 rounded-xl transition-colors cursor-pointer text-sm",
              location.pathname === "/arkadasini-oner"
                ? "bg-myGreen/10 text-myGreen font-semibold"
                : "text-myGreen/60 hover:text-myGreen hover:bg-myGreen/5"
            )}
          >
            <Gift size={16} weight={location.pathname === "/arkadasini-oner" ? "fill" : "regular"} />
            <span>Arkadaşını Öner</span>
          </Link>
          {user && (
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-full bg-myBlue/15 flex items-center justify-center text-[11px] font-bold text-myBlue shrink-0">
                {initials}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{user.shortName || "guest"}</p>
                <p className="text-[10px] text-white/20 truncate">Öğrenci ID: {user.id}</p>
              </div>
            </div>
          )}
          <button
            onClick={logout}
            className="flex items-center gap-3 px-3 py-2 rounded-xl text-white/25 hover:text-white/50 hover:bg-white/5 transition-colors cursor-pointer w-full text-sm"
          >
            <SignOut size={16} />
            <span>Çıkış Yap</span>
          </button>
        </div>
      </aside>
    </>
  );
}

export function SidebarToggle({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="lg:hidden fixed top-3 left-3 z-10 p-2.5 bg-surface-1 border border-white/[0.08] rounded-xl cursor-pointer text-white/50 hover:text-white/80 transition-colors"
    >
      <List size={20} />
    </button>
  );
}
